export class NetworkTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`Request timed out after ${timeoutMs}ms`);
    this.name = 'NetworkTimeoutError';
  }
}

export const isTransportError = (e: unknown): boolean => {
  if (e instanceof NetworkTimeoutError) return true;
  // Browsers surface a fetch/network failure as a TypeError. Message text varies
  // by browser:
  //   Chrome:  "Failed to fetch"
  //   Firefox: "NetworkError when attempting to fetch resource"
  //   Safari:  "Load failed"
  // The regex is intentionally narrow to avoid misclassifying unrelated
  // TypeErrors — in particular, Vue Router lazy-chunk failures surface as
  // "Failed to load dynamically imported module: …" which must NOT be treated
  // as a transport error and re-queued.
  if (e instanceof TypeError && /\bfetch\b|network|load failed/i.test(e.message)) return true;
  return false;
};

/**
 * Wraps a signal-aware async function in an AbortController + timeout.
 * Throws NetworkTimeoutError on timeout; re-throws all other errors.
 *
 * The caller MUST thread the provided AbortSignal into fetch/mande/etc options
 * for cancellation to actually take effect. Without that, the timeout will
 * still fire NetworkTimeoutError after `timeoutMs` but the underlying request
 * keeps running in the background.
 */
export async function fetchWithTimeout<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number
): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fn(controller.signal);
  } catch (e) {
    if (controller.signal.aborted) {
      throw new NetworkTimeoutError(timeoutMs);
    }
    throw e;
  } finally {
    clearTimeout(timeoutId);
  }
}

export const withTimeout = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new NetworkTimeoutError(timeoutMs)), timeoutMs);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
};
